import React from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { makeStyles, InputAdornment } from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { useEnqueueStacks } from '@avangard/utils/hooks';
import { Drawer } from '@avangard/ui/core';
import { TextField } from '@avangard/ui/core';
import { white, red } from '@avangard/ui/colors';

import { useCurrentUser } from '@modules/user/hooks';
import { ChatEntity } from '@modules/chat/entities';
import { ChatsList, ChatsMessages, useChatsManager } from '@modules/chat/organisms';
import { GetChatsQuery, SendChatMessageMutation } from '@modules/chat/graphql';

import type {
    SendChatMessageMutationType,
    SendChatMessageMutationVariables,
} from '@modules/types/graphql';

type ChatsDrawerProps = {
    chats: ChatEntity[];
    open: boolean;
    onClose: () => void;
};

const sendIcon = <Send style={{ fontSize: 16 }} />;

const ChatsDrawer = (props: ChatsDrawerProps): React.ReactElement => {
    const { chats, open, onClose } = props;

    const { t } = useTranslation(['common', 'chats']);

    const drawerClasses = useDrawerStyles();
    const textFieldClasses = useTextFieldStyles();

    const { enqueueError } = useEnqueueStacks();

    const { filters } = useChatsManager();

    const { currentUser } = useCurrentUser();

    const [chosenChatId, setChosenChatId] = React.useState<string | null>(null);

    const handleChooseChat = (chatId: string) => setChosenChatId(chatId);
    const handleResetChat = () => setChosenChatId(null);

    const [message, setMessage] = React.useState('');
    const [messageError, setMessageError] = React.useState(false);

    const handleChangeMessage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setMessage(value);

        if (value) {
            setMessageError(false);
        }
    };

    const [sendChatMessage, { loading: sendChatMessageLoading }] = useMutation<
        SendChatMessageMutationType,
        SendChatMessageMutationVariables
    >(SendChatMessageMutation);

    const handleSendChatMessage = (chatId: string) => async (event: React.FormEvent) => {
        event.preventDefault();

        try {
            if (!chatId) {
                throw new Error();
            }

            if (!message) {
                setMessageError(true);

                throw new Error();
            }

            await sendChatMessage({
                variables: { message, chatId },
                refetchQueries: [
                    {
                        query: GetChatsQuery,
                        variables: {
                            chatId: filters?.chatId,
                            type: filters?.type,
                            entityId: filters?.entityId,
                            ownerId: filters?.ownerId,
                        },
                    },
                ],
                awaitRefetchQueries: true,
            });

            setMessage('');
            setMessageError(false);
        } catch {
            enqueueError(t('chats:notifiers.send_message.error'), {
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            });
        }
    };

    const chosenChat = chats.find(chat => chosenChatId === chat.id);

    const title = React.useMemo(() => {
        if (chosenChat) {
            const chatTitle = chosenChat.getChatTitle(currentUser);

            return chatTitle;
        }

        if (chats.length === 1) {
            const [firstChat] = chats;

            const chatTitle = firstChat.getChatTitle(currentUser);

            return chatTitle;
        }

        if (chats.length > 1) {
            return t('chats:titles.chats');
        }

        return '';
    }, [t, chats, chosenChat, currentUser]);

    const renderDrawerContent = (
        chat: ChatEntity,
        BodyComponent: React.FunctionComponent<any>,
        FooterComponent: React.FunctionComponent<any>,
    ) => {
        const handleSendMessage = handleSendChatMessage(chat.id);

        return (
            <>
                <BodyComponent className='body'>
                    <ChatsMessages groupsMessages={chat.getGroupedMessageByDate()} />
                </BodyComponent>

                <FooterComponent className='footer'>
                    <Form onSubmit={handleSendMessage}>
                        <TextField
                            fullWidth
                            classes={textFieldClasses}
                            name='chat-send-message'
                            placeholder={t('common:forms.placeholder.message') ?? ''}
                            disabled={sendChatMessageLoading}
                            value={message}
                            error={messageError}
                            onChange={handleChangeMessage}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end' onClick={handleSendMessage}>
                                        {sendIcon}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Form>
                </FooterComponent>
            </>
        );
    };

    const handleClose = () => {
        if (chosenChat) {
            handleResetChat();
            return;
        }

        onClose();
    };

    return (
        <StyledDrawer
            externalClasses={drawerClasses}
            title={title}
            open={open}
            onClose={handleClose}
        >
            {({ BodyComponent, FooterComponent }) => {
                if (chosenChat) {
                    return renderDrawerContent(chosenChat, BodyComponent, FooterComponent);
                }

                if (chats.length === 1) {
                    const [firstChat] = chats;

                    return renderDrawerContent(firstChat, BodyComponent, FooterComponent);
                }

                return <ChatsList chats={chats} onChooseChat={handleChooseChat} />;
            }}
        </StyledDrawer>
    );
};

const useDrawerStyles = makeStyles({
    title: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
});

const useTextFieldStyles = makeStyles({
    root: {
        '&:hover': {
            '& .MuiInputAdornment-root': {
                color: red[100],
            },
        },
    },
});

const StyledDrawer = styled(Drawer)`
    & .body {
        padding: 0 0 64px 0;
    }

    & .footer {
        position: absolute;
        bottom: 0;
        background-color: ${white[100]};
    }
`;

const Form = styled.form`
    width: 100%;
`;

export { ChatsDrawer };
